import React, { useState, useRef, useMemo, useEffect } from 'react';
import { ContractClient } from '../contract';
import { ReadOnlyContractClient } from '../contract-readonly';

interface TrustGroup {
  owner: string;
  name: string;
  members: string[];
  created_at: number;
}

interface TrustGroupManagerProps {
  contractClient: ContractClient | null;
  readOnlyClient: ReadOnlyContractClient | null;
  walletAddress: string | null;
  onClose: () => void;
  onCreatePrivateSession?: (trustGroupId: number) => Promise<void>;
  /**
   * Optional: parent can use this to filter map markers / “nearby” lists to wallets
   * that appear in the currently visible trust groups (respects filter chips).
   */
  onTrustGroupMemberFilterChange?: (memberAddresses: string[]) => void;
}

const chipBase: React.CSSProperties = {
  minHeight: 40,
  padding: '8px 12px',
  borderRadius: 9999,
  border: '1px solid #cbd5e1',
  backgroundColor: '#fff',
  fontSize: 13,
  fontWeight: 600,
  cursor: 'pointer',
  flexShrink: 0,
};

export const TrustGroupManager: React.FC<TrustGroupManagerProps> = ({
  contractClient,
  readOnlyClient,
  walletAddress,
  onClose,
  onCreatePrivateSession,
  onTrustGroupMemberFilterChange,
}) => {
  const [myTrustGroups, setMyTrustGroups] = useState<number[]>([]);
  const [trustGroups, setTrustGroups] = useState<Map<number, TrustGroup>>(new Map());
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [newGroupName, setNewGroupName] = useState('');
  const [newMemberAddress, setNewMemberAddress] = useState('');
  const loadingRef = useRef(false);
  /** `null` = show all loaded groups; otherwise only ids in the set */
  const [filterGroupIds, setFilterGroupIds] = useState<Set<number> | null>(null);
  /** Avoid pushing an empty allowlist to the parent before the user has loaded groups once */
  const [hasSyncedMemberFilter, setHasSyncedMemberFilter] = useState(false);

  const displayedGroupIds = useMemo(() => {
    if (filterGroupIds === null) return myTrustGroups;
    return myTrustGroups.filter((id) => filterGroupIds.has(id));
  }, [myTrustGroups, filterGroupIds]);

  useEffect(() => {
    if (!onTrustGroupMemberFilterChange || !hasSyncedMemberFilter) return;
    if (myTrustGroups.length === 0) {
      onTrustGroupMemberFilterChange([]);
      return;
    }
    const addrs = new Set<string>();
    for (const id of displayedGroupIds) {
      const g = trustGroups.get(id);
      if (!g) continue;
      if (g.owner) addrs.add(String(g.owner));
      for (const m of g.members) addrs.add(String(m));
    }
    onTrustGroupMemberFilterChange(Array.from(addrs));
  }, [displayedGroupIds, trustGroups, onTrustGroupMemberFilterChange, hasSyncedMemberFilter, myTrustGroups.length]);

  const loadMyTrustGroups = async () => {
    if ((!readOnlyClient && !contractClient) || !walletAddress || loadingRef.current) return;

    loadingRef.current = true;
    setLoading(true);
    setError(null);
    try {
      // GeoSession V2 has no list_my_trust_groups — scan get_trust_group (read-only, no wallet).
      const groupIds = readOnlyClient
        ? await readOnlyClient.listTrustGroupIdsForWallet(walletAddress)
        : await contractClient!.listMyTrustGroups();
      setMyTrustGroups(groupIds || []);
      setFilterGroupIds(null);

      const client = readOnlyClient || contractClient!;
      const groupsMap = new Map<number, TrustGroup>();
      await Promise.all(
        (groupIds || []).map(async (groupId) => {
          try {
            const group = await client.getTrustGroup(groupId);
            if (group) {
              const normalizedGroup: TrustGroup = {
                owner: group.owner || group.admin || '',
                name: group.name || '',
                members: Array.isArray(group.members) ? group.members : group.members ? [group.members] : [],
                created_at: group.created_at || group.createdAt || 0,
              };
              groupsMap.set(groupId, normalizedGroup);
            }
          } catch {
            /* skip */
          }
        }),
      );
      setTrustGroups(groupsMap);
    } catch (err: any) {
      const errorMessage = err?.message || err?.toString() || String(err) || 'Unknown error';
      if (errorMessage.includes('rejected') || errorMessage.includes('denied') || errorMessage.includes('Transaction was rejected')) {
        setError('Please approve the transaction to continue.');
      } else {
        setError(errorMessage || 'Failed to load trust groups');
      }
    } finally {
      setHasSyncedMemberFilter(true);
      setLoading(false);
      loadingRef.current = false;
    }
  };

  const handleCreateGroup = async () => {
    if (!contractClient || !newGroupName.trim()) {
      setError('Please enter a group name');
      return;
    }

    setLoading(true);
    setError(null);
    try {
      await contractClient.createTrustGroup(newGroupName.trim());
      setNewGroupName('');
      setShowCreateForm(false);
      await loadMyTrustGroups();
    } catch (err: any) {
      setError(err.message || 'Failed to create trust group');
    } finally {
      setLoading(false);
    }
  };

  const handleAddMember = async (groupId: number) => {
    if (!contractClient || !newMemberAddress.trim()) {
      setError('Please enter a valid Stellar address');
      return;
    }

    if (newMemberAddress.length !== 56 || (!newMemberAddress.startsWith('G') && !newMemberAddress.startsWith('C'))) {
      setError('Invalid Stellar address format');
      return;
    }

    setLoading(true);
    setError(null);
    try {
      await contractClient.addToTrustGroup(groupId, newMemberAddress.trim());
      setNewMemberAddress('');
      await loadMyTrustGroups();
    } catch (err: any) {
      setError(err.message || 'Failed to add member');
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveMember = async (groupId: number, memberAddress: string) => {
    if (!contractClient) return;
    if (!window.confirm(`Remove ${memberAddress.slice(0, 8)}…${memberAddress.slice(-4)} from this trust group?`)) {
      return;
    }

    setLoading(true);
    setError(null);
    try {
      await contractClient.removeFromTrustGroup(groupId, memberAddress);
      await loadMyTrustGroups();
    } catch (err: any) {
      setError(err.message || 'Failed to remove member');
    } finally {
      setLoading(false);
    }
  };

  const handleCreatePrivateSession = async (groupId: number) => {
    if (!contractClient || !onCreatePrivateSession) return;

    setLoading(true);
    setError(null);
    try {
      await onCreatePrivateSession(groupId);
      onClose();
    } catch (err: any) {
      setError(err.message || 'Failed to create private session');
    } finally {
      setLoading(false);
    }
  };

  const toggleFilterGroup = (groupId: number) => {
    setFilterGroupIds((prev) => {
      if (prev === null) {
        return new Set([groupId]);
      }
      const next = new Set(prev);
      if (next.has(groupId)) next.delete(groupId);
      else next.add(groupId);
      if (next.size === 0) return null;
      return next;
    });
  };

  if ((!readOnlyClient && !contractClient) || !walletAddress) {
    return null;
  }

  const shell: React.CSSProperties = {
    width: '100%',
    maxWidth: 'min(560px, 100vw - 16px)',
    maxHeight: 'min(90dvh, 880px)',
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
    boxSizing: 'border-box',
    margin: '0 auto',
    borderRadius: 12,
    backgroundColor: '#fff',
    boxShadow: '0 16px 48px rgba(0,0,0,0.2)',
  };

  const scrollBody: React.CSSProperties = {
    flex: 1,
    overflowY: 'auto',
    WebkitOverflowScrolling: 'touch',
    padding: '12px clamp(12px, 4vw, 20px) max(16px, env(safe-area-inset-bottom))',
  };

  return (
    <div className="overlay-content trust-group-overlay" style={shell}>
      <div
        style={{
          flexShrink: 0,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 12,
          padding: '14px clamp(12px, 4vw, 18px)',
          borderBottom: '1px solid #e2e8f0',
          backgroundColor: '#f8fafc',
        }}
      >
        <h2 style={{ margin: 0, fontSize: 'clamp(17px, 4.5vw, 22px)', fontWeight: 700, color: '#0f172a' }}>Trust Groups</h2>
        <button
          type="button"
          onClick={onClose}
          aria-label="Close"
          style={{
            minWidth: 44,
            minHeight: 44,
            background: 'none',
            border: 'none',
            fontSize: 26,
            lineHeight: 1,
            cursor: 'pointer',
            color: '#64748b',
            borderRadius: 8,
          }}
        >
          ×
        </button>
      </div>

      <div style={scrollBody}>
        <div
          style={{
            marginBottom: 14,
            padding: 12,
            backgroundColor: '#eff6ff',
            borderRadius: 8,
            border: '1px solid #bfdbfe',
            fontSize: 13,
            color: '#1e3a5f',
            lineHeight: 1.45,
          }}
        >
          <strong style={{ display: 'block', marginBottom: 6, color: '#1d4ed8' }}>What you can do</strong>
          <ul style={{ margin: 0, paddingLeft: 18 }}>
            <li>Create a named group on the session contract (you are the owner).</li>
            <li>Add or remove Stellar addresses (classic G… or smart C…) as members.</li>
            <li>Start a private session gated to one group (only members can join).</li>
          </ul>
          <p style={{ margin: '10px 0 0', fontSize: 12, color: '#64748b' }}>
            Loading uses read-only RPC (no wallet prompt). Creating groups and editing members still signs with your wallet.
          </p>
          <p style={{ margin: '8px 0 0', fontSize: 12, color: '#64748b' }}>
            Use <strong>Show groups</strong> chips to choose which groups feed the map. In the game panel, tap the shield next to “Show others nearby” to show only those wallets on the map (compact markers).
          </p>
        </div>

        {error && (
          <div
            style={{
              padding: '10px 12px',
              backgroundColor: '#fef2f2',
              color: '#b91c1c',
              borderRadius: 8,
              marginBottom: 12,
              fontSize: 13,
              wordBreak: 'break-word',
              maxHeight: 140,
              overflowY: 'auto',
            }}
          >
            {error}
          </div>
        )}

        {contractClient && (
          <div style={{ marginBottom: 14 }}>
            <button
              type="button"
              onClick={() => setShowCreateForm(!showCreateForm)}
              className="primary-button"
              style={{ width: '100%', minHeight: 44, fontSize: 15, fontWeight: 600 }}
            >
              {showCreateForm ? 'Cancel' : '+ Create New Trust Group'}
            </button>

            {showCreateForm && (
              <div style={{ marginTop: 10, padding: 14, backgroundColor: '#f1f5f9', borderRadius: 8 }}>
                <input
                  type="text"
                  placeholder="Trust group name"
                  value={newGroupName}
                  onChange={(e) => setNewGroupName(e.target.value)}
                  style={{
                    width: '100%',
                    minHeight: 44,
                    padding: '10px 12px',
                    marginBottom: 10,
                    borderRadius: 8,
                    border: '1px solid #cbd5e1',
                    fontSize: 16,
                    boxSizing: 'border-box',
                  }}
                />
                <button
                  type="button"
                  onClick={handleCreateGroup}
                  className="primary-button"
                  disabled={loading || !newGroupName.trim()}
                  style={{ width: '100%', minHeight: 44 }}
                >
                  Create group
                </button>
              </div>
            )}
          </div>
        )}

        {!loading && myTrustGroups.length === 0 && !error && (
          <div style={{ textAlign: 'center', padding: '16px 8px', color: '#64748b' }}>
            <p style={{ marginBottom: 14, fontSize: 15 }}>Load groups you own or belong to (scans on-chain records).</p>
            <button
              type="button"
              onClick={loadMyTrustGroups}
              className="primary-button"
              disabled={loading}
              style={{ minHeight: 44, padding: '0 24px', fontSize: 15, fontWeight: 600 }}
            >
              Load my trust groups
            </button>
          </div>
        )}

        {loading && myTrustGroups.length === 0 && (
          <div style={{ textAlign: 'center', padding: 24, color: '#64748b' }}>Loading trust groups…</div>
        )}

        {!loading && myTrustGroups.length === 0 && error && (
          <div style={{ textAlign: 'center', padding: 16 }}>
            <p style={{ color: '#64748b', marginBottom: 12, fontSize: 14 }}>{error}</p>
            <button type="button" onClick={loadMyTrustGroups} className="primary-button" style={{ minHeight: 44, padding: '0 20px' }}>
              Retry
            </button>
          </div>
        )}

        {myTrustGroups.length > 0 && (
          <div style={{ marginBottom: 14 }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 8 }}>
              Show groups
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center' }}>
              <button
                type="button"
                onClick={() => setFilterGroupIds(null)}
                style={{
                  ...chipBase,
                  borderColor: filterGroupIds === null ? '#2563eb' : '#cbd5e1',
                  backgroundColor: filterGroupIds === null ? '#dbeafe' : '#fff',
                  color: filterGroupIds === null ? '#1e40af' : '#334155',
                }}
              >
                All
              </button>
              {myTrustGroups.map((groupId) => {
                const g = trustGroups.get(groupId);
                const label = (g?.name || `Group #${groupId}`).slice(0, 22);
                const picked = filterGroupIds !== null && filterGroupIds.has(groupId);
                const faded = filterGroupIds !== null && !filterGroupIds.has(groupId);
                return (
                  <button
                    type="button"
                    key={groupId}
                    onClick={() => toggleFilterGroup(groupId)}
                    style={{
                      ...chipBase,
                      opacity: faded ? 0.45 : 1,
                      borderColor: picked ? '#2563eb' : '#cbd5e1',
                      backgroundColor: picked ? '#dbeafe' : '#fff',
                      color: picked ? '#1e40af' : '#334155',
                      maxWidth: '100%',
                    }}
                    title={`#${groupId}`}
                  >
                    {label}
                  </button>
                );
              })}
            </div>
            <p style={{ fontSize: 11, color: '#94a3b8', marginTop: 8, marginBottom: 0 }}>
              Tap “All” to reset. Tap a name to show only that group; tap again to combine. Map “nearby” filters can use the member list from visible groups when wired in the parent.
            </p>
          </div>
        )}

        {displayedGroupIds.map((groupId) => {
          const group = trustGroups.get(groupId);
          if (!group) return null;

          const isOwner = String(group.owner) === String(walletAddress);

          return (
            <div
              key={groupId}
              style={{
                padding: 14,
                backgroundColor: '#f8fafc',
                borderRadius: 10,
                marginBottom: 12,
                border: '1px solid #e2e8f0',
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 10, marginBottom: 10, flexWrap: 'wrap' }}>
                <h3 style={{ margin: 0, fontSize: 17, color: '#0f172a', wordBreak: 'break-word' }}>{group.name || `Group #${groupId}`}</h3>
                <span style={{ fontSize: 12, color: '#64748b', flexShrink: 0 }}>ID {groupId}</span>
              </div>

              <div style={{ marginBottom: 10, fontSize: 13, color: '#475569' }}>
                <strong>Owner:</strong> {group.owner ? `${group.owner.slice(0, 6)}…${group.owner.slice(-4)}` : '—'}
              </div>

              <div style={{ marginBottom: 10 }}>
                <strong style={{ fontSize: 13, color: '#334155' }}>Members ({group.members.length})</strong>
                <div style={{ marginTop: 6, maxHeight: 160, overflowY: 'auto' }}>
                  {group.members.map((member, idx) => (
                    <div
                      key={idx}
                      style={{
                        padding: '8px 10px',
                        backgroundColor: member === walletAddress ? '#e0f2fe' : '#fff',
                        marginBottom: 6,
                        borderRadius: 6,
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        gap: 8,
                        flexWrap: 'wrap',
                        border: '1px solid #e2e8f0',
                      }}
                    >
                      <span style={{ fontSize: 12, fontFamily: 'monospace', wordBreak: 'break-all' }}>
                        {member.slice(0, 6)}…{member.slice(-4)}
                        {member === walletAddress && <span style={{ marginLeft: 6, color: '#0369a1', fontWeight: 600 }}>(You)</span>}
                        {member === group.owner && <span style={{ marginLeft: 6, color: '#15803d', fontWeight: 600 }}>(Owner)</span>}
                      </span>
                      {isOwner && member !== group.owner && contractClient && (
                        <button
                          type="button"
                          onClick={() => handleRemoveMember(groupId, member)}
                          style={{
                            minHeight: 36,
                            padding: '6px 12px',
                            fontSize: 12,
                            fontWeight: 600,
                            backgroundColor: '#dc2626',
                            color: '#fff',
                            border: 'none',
                            borderRadius: 6,
                            cursor: 'pointer',
                          }}
                          disabled={loading}
                        >
                          Remove
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              {isOwner && contractClient && (
                <div style={{ marginTop: 10, padding: 12, backgroundColor: '#fff', borderRadius: 8, border: '1px solid #e2e8f0' }}>
                  <input
                    type="text"
                    inputMode="text"
                    autoComplete="off"
                    placeholder="Member address (G… or C…)"
                    value={newMemberAddress}
                    onChange={(e) => setNewMemberAddress(e.target.value)}
                    style={{
                      width: '100%',
                      minHeight: 44,
                      padding: '10px 12px',
                      marginBottom: 8,
                      borderRadius: 8,
                      border: '1px solid #cbd5e1',
                      fontSize: 14,
                      boxSizing: 'border-box',
                    }}
                  />
                  <button
                    type="button"
                    onClick={() => handleAddMember(groupId)}
                    className="primary-button"
                    disabled={loading || !newMemberAddress.trim()}
                    style={{ width: '100%', minHeight: 44, fontSize: 14 }}
                  >
                    Add member
                  </button>
                </div>
              )}

              {onCreatePrivateSession && contractClient && (
                <button
                  type="button"
                  onClick={() => handleCreatePrivateSession(groupId)}
                  className="primary-button"
                  disabled={loading}
                  style={{ width: '100%', marginTop: 10, minHeight: 44, fontSize: 14 }}
                >
                  Create private session
                </button>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};
